/**
 * What the school's redraft changed.
 *
 * The whole surface is one claim: these fifteen are worth your evening and
 * those four hundred are not. Everything about the layout is an argument for
 * that claim being checkable rather than trusted.
 *
 *   · The substantive findings are open. Not a list of clause numbers to click
 *     through — the reason and the redline, on the page, in the order the
 *     import ranked them. A rep with twenty minutes before a meeting reads
 *     downwards and stops when they run out of time, and what they read is the
 *     most-moved provisions rather than clause 3.1.
 *   · The editorial tail is one line with a count, and every row inside it
 *     opens to its full redline. A summary nobody can check is a summary that
 *     has to be trusted, and the classifier behind this one is a hundred lines
 *     of regular expressions.
 *   · The reason is never a conclusion. "Obligation words changed — an
 *     obligation may have been softened" is a prompt to read the redline
 *     underneath it, and the redline is directly underneath it for that reason.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { draftApi, type DraftChange, type RedlineSegment } from './api.js';

const STATUS_LABEL: Record<DraftChange['status'], string> = {
  removed: 'Deleted',
  amended: 'Amended',
  added: 'New',
  moved: 'Renumbered',
};

export function DraftMonitor({ onOpenClause }: { onOpenClause?: (number: string) => void }) {
  const drafts = useQuery({ queryKey: ['drafts'], queryFn: draftApi.list });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const rows = drafts.data?.drafts ?? [];
  const activeId = selectedId && rows.some((d) => d.id === selectedId) ? selectedId : rows[0]?.id;

  /*
   * The two empty states, as findings rather than as errors.
   *
   * Both were set as an alarm — one a defect-hued `.callout`, and neither
   * describes anything wrong. "No redraft has arrived yet" is the ordinary
   * state of this surface for most of a negotiation, and a red-edged box says
   * the software is broken to a rep who opens the tab to check.
   */
  if (drafts.data && !drafts.data.available) {
    return (
      <div className="screen screen-column">
        <h1 className="text-[25px] font-semibold leading-[1.15] tracking-tight">Draft monitor</h1>
        <p className="measure mt-2 text-[13.5px] leading-relaxed text-[var(--color-muted)]">
          {drafts.data.unavailableBecause}
        </p>
      </div>
    );
  }

  if (drafts.data && rows.length === 0) {
    return (
      <div className="screen screen-column">
        <h1 className="text-[25px] font-semibold leading-[1.15] tracking-tight">Draft monitor</h1>
        <p className="measure mt-2 text-[13.5px] leading-relaxed text-[var(--color-muted)]">
          No redraft has been read yet. When the school sends one back, import it with{' '}
          <code className="clause-number rounded-[4px] border border-[var(--color-rule)] bg-[var(--color-subtle)] px-1 py-0.5 text-[11.5px]">
            pnpm --filter @tmea/ingest draft:import -- &lt;file&gt; --label &quot;…&quot;
          </code>{' '}
          and what moved will be listed here, substantive changes first.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0">
      {/*
        The rail Ask sets: a named heading over hairline cards, at the app's
        rail width rather than a hand-picked w-64. Unlabelled buttons separated
        by a rule gave a list of drafts no title, so a rep arriving here had to
        infer from the labels alone what the column was.
      */}
      <nav
        className="hidden w-[var(--rail-width)] shrink-0 flex-col border-r border-[var(--color-rule)] md:flex"
        aria-label="Drafts"
      >
        <div className="border-b border-[var(--color-rule)] px-3 py-3">
          <h2 className="mb-0.5 text-[12.5px] font-semibold">Redrafts received</h2>
          <p className="text-[11.5px] leading-snug text-[var(--color-muted)]">
            Each one compared against the agreement we hold, clause by clause.
          </p>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto p-3">
          {rows.map((draft) => (
            <button
              key={draft.id}
              onClick={() => setSelectedId(draft.id)}
              aria-current={draft.id === activeId}
              className="hit block w-full rounded-[6px] border px-2.5 py-2 text-left"
              style={{
                borderColor:
                  draft.id === activeId ? 'var(--color-ink)' : 'var(--color-rule)',
              }}
            >
              <span className="block text-[11.5px] leading-snug">{draft.label}</span>
              {/* Counts are figures. Tabular so the two numbers line up down
                  the rail, which is the only comparison this list supports. */}
              <span className="label mt-1 block">
                <span className="tabular-nums">{draft.substantive}</span> substantive ·{' '}
                <span className="tabular-nums">{draft.editorial}</span> editorial
              </span>
            </button>
          ))}
        </div>
      </nav>

      <div className="screen">
        {activeId ? (
          <DraftDetail key={activeId} id={activeId} {...(onOpenClause ? { onOpenClause } : {})} />
        ) : null}
      </div>
    </div>
  );
}

function DraftDetail({ id, onOpenClause }: { id: string; onOpenClause?: (n: string) => void }) {
  const detail = useQuery({ queryKey: ['draft', id], queryFn: () => draftApi.detail(id) });
  const [tailOpen, setTailOpen] = useState(false);

  if (!detail.data) return null;
  const { draft, changes } = detail.data;
  const substantive = changes.filter((c) => c.severity === 'substantive');
  const editorial = changes.filter((c) => c.severity === 'editorial');

  return (
    /*
     * A reading column, not `measure-wide`.
     *
     * What is read here is a redline of legal prose, sentence against
     * sentence — the same register as the agreement itself — and 92ch of
     * serif with deletions struck through it is a horizon, not a paragraph.
     * The metadata that earned Ledgers its extra width is a clause number and
     * one reason per card.
     */
    <div className="screen-column">
      <header>
        <h1 className="text-[25px] font-semibold leading-[1.15] tracking-tight">{draft.label}</h1>
        <p className="measure mt-2 text-[13.5px] leading-relaxed text-[var(--color-muted)]">
          {substantive.length === 0 ? (
            <>
              Nothing in this draft moved an obligation, a figure or a negation. That is a finding,
              not an absence — {editorial.length} provisions were still reworded.
            </>
          ) : (
            <>
              {substantive.length} changes are worth reading before the next meeting.{' '}
              {editorial.length} more were reworded without moving an obligation, a figure or a
              negation.
            </>
          )}
        </p>
      </header>

      {substantive.length > 0 ? (
        <section className="section-rule mt-6 pt-3">
          {/* The tail below names itself on its disclosure; this half had no
              header at all, so the cards began under the summary sentence with
              nothing saying which of the two counts they were. */}
          <h2 className="label mb-2.5">Substantive changes</h2>
          <div className="flex flex-col gap-3">
            {substantive.map((change) => (
              <ChangeCard
                key={change.id}
                change={change}
                {...(onOpenClause ? { onOpenClause } : {})}
              />
            ))}
          </div>
        </section>
      ) : null}

      {editorial.length > 0 ? (
        <section className="section-rule mt-8 pt-3">
          <button
            onClick={() => setTailOpen((open) => !open)}
            aria-expanded={tailOpen}
            className="hit label underline underline-offset-2"
          >
            {tailOpen ? '▾' : '▸'} <span className="tabular-nums">{editorial.length}</span>{' '}
            editorial changes
          </button>
          {tailOpen ? (
            <ul className="mt-2">
              {editorial.map((change) => (
                <EditorialRow key={change.id} draftId={id} change={change} />
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

function ChangeCard({
  change,
  onOpenClause,
}: {
  change: DraftChange;
  onOpenClause?: (n: string) => void;
}) {
  return (
    /* `.card`, which is the border, the 8px radius and the 12px inset in one
       token — this was `.panel` plus a second `border` declaration plus an
       inline colour, three ways of saying the thing the primitive already
       says. */
    <article className="card">
      <header className="card-header">
        <div className="flex min-w-0 items-baseline gap-2">
          {onOpenClause ? (
            <button
              onClick={() => onOpenClause(change.clauseNumber)}
              className="hit clause-number shrink-0 text-[12px] underline decoration-[var(--color-rule-strong)] underline-offset-2"
            >
              {change.clauseNumber}
            </button>
          ) : (
            <span className="clause-number shrink-0 text-[12px]">{change.clauseNumber}</span>
          )}
          <span className="min-w-0 text-[13.5px] font-semibold leading-snug tracking-tight">
            {change.heading}
          </span>
        </div>
        <span className="label shrink-0" style={{ color: 'var(--color-defect)' }}>
          {STATUS_LABEL[change.status]}
        </span>
      </header>

      {/* The reasons are why a person should look, never a conclusion about
          what the change means. The redline is immediately beneath for that
          reason: the sentence sends you to the text, not instead of it. */}
      <ul className="flex flex-col gap-0.5">
        {change.reasons.map((reason) => (
          <li key={reason} className="text-[12px] leading-snug text-[var(--color-muted)]">
            · {reason}
          </li>
        ))}
      </ul>

      {change.redline ? <Redline segments={change.redline} /> : null}
    </article>
  );
}

function EditorialRow({ draftId, change }: { draftId: string; change: DraftChange }) {
  const [open, setOpen] = useState(false);
  const full = useQuery({
    queryKey: ['draft-change', draftId, change.clauseNumber],
    queryFn: () => draftApi.change(draftId, change.clauseNumber),
    enabled: open,
  });

  return (
    <li className="row flex-col items-stretch">
      <button
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        aria-expanded={open}
        className="hit flex w-full items-baseline gap-2.5 text-left"
      >
        <span className="clause-number w-14 shrink-0 text-[12px]">{change.clauseNumber}</span>
        <span className="min-w-0 flex-1 truncate text-[12.5px]">{change.heading}</span>
        <span className="label shrink-0">{STATUS_LABEL[change.status]}</span>
      </button>
      {/* Indented into the clause-number gutter, so an open row reads as
          belonging to its number and the closed rows above stay a scannable
          column. */}
      {open ? (
        <div className="ml-[70px]">
          {full.data ? <Redline segments={full.data.redline} /> : null}
          {change.reasons.length > 0 ? (
            <ul className="mt-2 flex flex-col gap-0.5">
              {change.reasons.map((reason) => (
                <li key={reason} className="text-[12px] leading-snug text-[var(--color-muted)]">
                  · {reason}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/**
 * The redline, as it is read at the table: one text with the deletions struck
 * and the insertions marked.
 *
 * Never colour alone — a deletion is struck through and an insertion is
 * underlined, so the reading survives monochrome print and the photocopy
 * somebody takes into the meeting.
 */
function Redline({ segments }: { segments: RedlineSegment[] }) {
  return (
    /* At the reading register the agreement itself uses: this is the school's
       words against ours, which is document text and not interface. */
    <p className="measure register-reading mt-3 whitespace-pre-wrap text-[14px] leading-relaxed">
      {segments.map((segment, index) => {
        if (segment.kind === 'same') return <span key={index}>{segment.text}</span>;
        if (segment.kind === 'del') {
          return (
            <del key={index} style={{ color: 'var(--color-defect)' }}>
              {segment.text}
            </del>
          );
        }
        return (
          <ins
            key={index}
            className="rounded-[2px] px-0.5"
            style={{ background: 'var(--color-rule)', textDecoration: 'underline' }}
          >
            {segment.text}
          </ins>
        );
      })}
    </p>
  );
}
